import React, { useState, useEffect } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text, TextInput } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAppColors } from '../../hooks/useAppColors';
import { useTranslation } from 'react-i18next';
import DatePicker from '../DatePicker';
import {
  type SubUserFormData,
  type MemberPlanOffer,
  calculatePlanTermPrice,
  calculateOfferPrice,
  getDiscountText,
  getAllowedTermsForMemberPlan,
  getOfferForPlanTerm,
} from './billingUtils';
import { calculateAge, formatCurrency, weightToKg, heightToCm } from '../../utils/formatters';
import type { MembershipPlan } from '../../hooks/usePayments';
import PlanSelector from './PlanSelector';
import TermSelector from './TermSelector';

interface SubUserFormProps {
  plans: MembershipPlan[] | undefined;
  onSubmit: (data: SubUserFormData & { planId: string; termMonths: number }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  /** Override the "select plan" label */
  selectPlanLabel?: string;
  /** Show term pricing on plans */
  showTermPricing?: boolean;
  /** Catalog offers for this member — used to restrict which terms are shown */
  offers?: MemberPlanOffer[];
}

export default function SubUserForm({
  plans,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel,
  selectPlanLabel,
  showTermPricing = false,
  offers = [],
}: SubUserFormProps) {
  const { t } = useTranslation();
  const colors = useAppColors();

  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState<SubUserFormData>({
    firstName: '',
    lastName: '',
    dob: null,
    weightKg: null,
    heightCm: null,
    measurementUnit: 'metric',
  });
  const [planId, setPlanId] = useState<string | null>(null);
  const [termMonths, setTermMonths] = useState<number>(0);
  const [weightInput, setWeightInput] = useState('');
  const [heightInput, setHeightInput] = useState('');

  // Compute allowed terms whenever the selected plan changes
  const allowedTerms = getAllowedTermsForMemberPlan(offers, planId);

  // Auto-select the first allowed term whenever the plan (and thus allowedTerms) changes
  useEffect(() => {
    if (allowedTerms.length > 0) {
      setTermMonths(allowedTerms[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const canContinueStep1 = !!form.firstName.trim() && !!form.lastName.trim() && !!form.dob;
  const validTerm = allowedTerms.includes(termMonths);

  const handleSubmit = () => {
    if (!planId || !form.dob || !validTerm) return;
    onSubmit({ ...form, planId, termMonths });
  };

  const handleCancel = () => {
    setForm({ firstName: '', lastName: '', dob: null, weightKg: null, heightCm: null, measurementUnit: 'metric' });
    setWeightInput('');
    setHeightInput('');
    setPlanId(null);
    setTermMonths(0);
    setStep(1);
    onCancel();
  };

  return (
    <View style={styles.container}>
      {step === 1 ? (
        <>
          <View style={styles.row}>
            <TextInput
              label={t('auth.firstName')}
              value={form.firstName}
              onChangeText={(text) => setForm((prev) => ({ ...prev, firstName: text }))}
              mode="outlined"
              style={[styles.input, styles.halfInput]}
              outlineColor={colors.border}
              activeOutlineColor={colors.text}
              textColor={colors.text}
            />
            <TextInput
              label={t('auth.lastName')}
              value={form.lastName}
              onChangeText={(text) => setForm((prev) => ({ ...prev, lastName: text }))}
              mode="outlined"
              style={[styles.input, styles.halfInput]}
              outlineColor={colors.border}
              activeOutlineColor={colors.text}
              textColor={colors.text}
            />
          </View>
          <DatePicker
            label={t('auth.dateOfBirth')}
            value={form.dob}
            onChange={(date) => setForm((prev) => ({ ...prev, dob: date }))}
            maximumDate={new Date()}
            minimumDate={new Date(1900, 0, 1)}
          />

          {/* Body Measurements (optional) */}
          <Text style={[styles.fieldHint, { color: colors.textSecondary }]}>
            {t('profile.bodyMeasurementsHint')}
          </Text>
          <View style={styles.row}>
            <TextInput
              label={`${t('profile.weight')} (${form.measurementUnit === 'metric' ? 'kg' : 'lbs'})`}
              value={weightInput}
              onChangeText={(v) => {
                setWeightInput(v);
                const val = parseFloat(v);
                setForm((prev) => ({
                  ...prev,
                  weightKg: !isNaN(val) ? weightToKg(val, prev.measurementUnit) : null,
                }));
              }}
              mode="outlined"
              keyboardType="decimal-pad"
              style={[styles.input, { flex: 1 }]}
              outlineColor={colors.border}
              activeOutlineColor={colors.text}
              textColor={colors.text}
            />
            <TextInput
              label={`${t('profile.height')} (${form.measurementUnit === 'metric' ? 'cm' : 'in'})`}
              value={heightInput}
              onChangeText={(v) => {
                setHeightInput(v);
                const val = parseFloat(v);
                setForm((prev) => ({
                  ...prev,
                  heightCm: !isNaN(val) ? heightToCm(val, prev.measurementUnit) : null,
                }));
              }}
              mode="outlined"
              keyboardType="decimal-pad"
              style={[styles.input, { flex: 1 }]}
              outlineColor={colors.border}
              activeOutlineColor={colors.text}
              textColor={colors.text}
            />
            <TouchableOpacity
              style={[styles.unitToggleBtn, { alignSelf: 'center' }]}
              onPress={() => {
                const newUnit = form.measurementUnit === 'metric' ? 'imperial' : 'metric';
                setForm((prev) => ({ ...prev, measurementUnit: newUnit }));
                // Clear inputs when switching units to avoid confusion
                setWeightInput('');
                setHeightInput('');
              }}
            >
              <Text style={[styles.unitToggleText, { color: colors.accent }]}>
                {form.measurementUnit === 'metric' ? t('profile.metric') : t('profile.imperial')}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.buttons}>
            <TouchableOpacity
              style={[styles.cancelBtn, { borderColor: colors.border }]}
              onPress={handleCancel}
              activeOpacity={0.7}
            >
              <Text style={[styles.cancelBtnText, { color: colors.textSecondary }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.confirmBtn, { backgroundColor: colors.accent, opacity: canContinueStep1 ? 1 : 0.5 }]}
              onPress={() => setStep(2)}
              disabled={!canContinueStep1}
              activeOpacity={0.7}
            >
              <Text style={[styles.confirmBtnText, { color: colors.accentText }]}>{t('common.continue')}</Text>
            </TouchableOpacity>
          </View>
        </>
      ) : (
        <>
          <View style={[styles.stepHeader, { backgroundColor: colors.bg, borderColor: colors.border }]}>
            <View style={styles.stepHeaderRow}>
              <View>
                <Text style={[styles.stepName, { color: colors.text }]}>{form.firstName} {form.lastName}</Text>
                {form.dob && (
                  <Text style={[styles.stepDob, { color: colors.textSecondary }]}>
                    {t('profile.age', { count: calculateAge(form.dob) })}
                  </Text>
                )}
              </View>
              <TouchableOpacity onPress={() => setStep(1)} hitSlop={12}>
                <Icon name="pencil" size={18} color={colors.accent} />
              </TouchableOpacity>
            </View>

            <View style={[styles.sectionHeader, { borderBottomColor: colors.border }]}>
              <Text style={[styles.sectionLabel, { color: colors.textTertiary }]}>
                {selectPlanLabel || t('subscription.selectPlan')}
              </Text>
            </View>
            <PlanSelector
              plans={plans}
              dob={form.dob?.toISOString().split('T')[0] || null}
              selectedPlanId={planId}
              onSelectPlan={setPlanId}
              variant="row"
              selectedTermMonths={termMonths || undefined}
              showDiscountBadge={showTermPricing}
              offers={offers}
            />

            {/* Term selector — only shown once a plan has been selected */}
            {planId && (
              <TermSelector
                selectedTermMonths={termMonths}
                onSelectTerm={setTermMonths}
                allowedTermMonths={allowedTerms}
                compact
              />
            )}

            {planId && showTermPricing && termMonths > 0 && (() => {
              const selectedPlan = (plans || []).find((p) => p.id === planId);
              if (!selectedPlan) return null;
              const offer = getOfferForPlanTerm(offers, planId, termMonths);
              const pricing = calculateOfferPrice(selectedPlan, termMonths, offer);
              const hasSavings = pricing.discountAmount > 0;
              return (
                <View style={[styles.pricingSummary, { backgroundColor: colors.accent + '10', borderColor: colors.accent + '30' }]}>
                  <View style={styles.pricingSummaryRow}>
                    <View style={styles.pricingAmounts}>
                      {hasSavings && (
                        <Text style={[styles.pricingStrike, { color: colors.textTertiary }]}>
                          {formatCurrency(pricing.subtotal / termMonths)}
                        </Text>
                      )}
                      <View style={styles.pricingMain}>
                        <Text style={[styles.pricingFinal, { color: colors.text }]}>
                          {formatCurrency(pricing.finalAmount / termMonths)}
                        </Text>
                        <Text style={[styles.pricingTerm, { color: colors.textSecondary }]}>
                          /{t('billing.perMonth')}
                        </Text>
                      </View>
                    </View>
                    {hasSavings && pricing.discount && (
                      <View style={[styles.pricingDiscountBadge, { backgroundColor: colors.accent + '20' }]}>
                        <Text style={[styles.pricingDiscountText, { color: colors.accent }]}>
                          {getDiscountText(pricing.discount, pricing.subtotal, pricing.finalAmount, formatCurrency, t, termMonths)}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              );
            })()}
          </View>

          <View style={styles.buttons}>
            <TouchableOpacity
              style={[styles.cancelBtn, { borderColor: colors.border }]}
              onPress={() => setStep(1)}
              activeOpacity={0.7}
            >
              <Text style={[styles.cancelBtnText, { color: colors.textSecondary }]}>{t('common.back')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.confirmBtn, { backgroundColor: colors.accent, opacity: (!planId || isSubmitting || !validTerm) ? 0.5 : 1 }]}
              onPress={handleSubmit}
              disabled={!planId || isSubmitting || !validTerm}
              activeOpacity={0.7}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={colors.accentText} />
              ) : (
                <Text style={[styles.confirmBtnText, { color: colors.accentText }]}>
                  {submitLabel || t('subscription.addMember')}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginTop: 12 },
  row: { flexDirection: 'row', gap: 10 },
  input: { flex: 1 },
  halfInput: { flex: 1 },
  fieldHint: {
    fontSize: 12,
    marginBottom: 8,
    marginLeft: 4,
  },
  stepHeader: {
    flexDirection: 'column',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  stepHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  stepName: { fontSize: 15, fontWeight: '600' },
  stepDob: { fontSize: 13, marginTop: 2 },

  // Section Header with Divider (matches SubscriptionScreen)
  sectionHeader: {
    paddingBottom: 10,
    borderBottomWidth: 1,
    marginBottom: 6,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  unitToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  unitToggleText: {
    fontSize: 14,
    fontWeight: '600',
  },
  buttons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelBtnText: { fontSize: 14, fontWeight: '500' },
  confirmBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmBtnText: { fontSize: 14, fontWeight: '600' },

  pricingSummary: {
    marginTop: 10,
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  pricingSummaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 6,
  },
  pricingAmounts: {
    flexShrink: 1,
  },
  pricingStrike: {
    fontSize: 11,
    textDecorationLine: 'line-through',
  },
  pricingMain: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 2,
  },
  pricingFinal: {
    fontSize: 15,
    fontWeight: '700',
  },
  pricingTerm: {
    fontSize: 12,
  },
  pricingDiscountBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  pricingDiscountText: {
    fontSize: 11,
    fontWeight: '600',
  },
});
